import React, { useState, useEffect, useRef, useCallback } from 'react';
import { profileAPI, authAPI } from '../services/api';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { getAvatarColor, getInitials } from '../utils/avatarUtils';
import { PageLayout, Badge, ConfirmDialog } from './ui';

const SecurityKeysCard = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [renamingKey, setRenamingKey] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const loadKeys = useCallback(async () => {
    try {
      const data = await authAPI.getWebAuthnKeys();
      setKeys(data.keys || []);
    } catch (err) {
      setError(err.error || 'Failed to load security keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleRegister = async () => {
    if (!deviceName.trim()) return;
    setRegistering(true);
    setError('');
    try {
      const options = await authAPI.getWebAuthnRegisterOptions();
      const { startRegistration } = await import('../utils/webauthn');
      const credential = await startRegistration(options);
      const result = await authAPI.verifyWebAuthnRegistration(credential, deviceName.trim());
      if (result.recovery_codes) {
        setRecoveryCodes(result.recovery_codes);
      }
      setShowRegister(false);
      setDeviceName('');
      await loadKeys();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Security key operation was cancelled or timed out.');
      } else {
        setError(err.error || err.message || 'Failed to register security key');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (keyId, keyName) => {
    if (!window.confirm(`Remove security key "${keyName}"? This cannot be undone.`)) return;
    try {
      await authAPI.deleteWebAuthnKey(keyId);
      await loadKeys();
    } catch (err) {
      setError(err.error || 'Failed to delete key');
    }
  };

  const handleRename = async (keyId) => {
    if (!renameValue.trim()) return;
    try {
      await authAPI.renameWebAuthnKey(keyId, renameValue.trim());
      setRenamingKey(null);
      setRenameValue('');
      await loadKeys();
    } catch (err) {
      setError(err.error || 'Failed to rename key');
    }
  };

  const webauthnSupported = typeof window !== 'undefined' && window.PublicKeyCredential;

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Security Keys</h3>
          <p className="text-sm text-gray-500 mt-1">YubiKey or other FIDO2 security keys for two-factor authentication</p>
        </div>
        {webauthnSupported && (
          <button
            onClick={() => setShowRegister(true)}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover"
          >
            Add Key
          </button>
        )}
      </div>

      <div className="p-6 space-y-4">
        {error && <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700">{error}</div>}

        {!webauthnSupported && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm text-yellow-700">
            Your browser does not support security keys (WebAuthn).
          </div>
        )}

        {recoveryCodes && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-green-900 mb-2">Recovery Codes</h4>
            <p className="text-xs text-green-700 mb-3">Save these codes in a safe place. Each can be used once if you lose your security key.</p>
            <div className="grid grid-cols-2 gap-1 font-mono text-sm">
              {recoveryCodes.map((code, i) => <div key={i} className="bg-white px-2 py-1 rounded border border-green-200">{code}</div>)}
            </div>
            <button onClick={() => setRecoveryCodes(null)} className="mt-3 text-xs text-green-700 underline">I've saved these codes</button>
          </div>
        )}

        {showRegister && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <label className="block text-sm font-medium text-gray-700">Key Name</label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder='e.g. "YubiKey 5 NFC"'
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary"
              maxLength={255}
            />
            <div className="flex gap-2">
              <button
                onClick={handleRegister}
                disabled={registering || !deviceName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
              >
                {registering ? 'Touch your key...' : 'Register Key'}
              </button>
              <button onClick={() => { setShowRegister(false); setDeviceName(''); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-500 py-4">Loading...</div>
        ) : keys.length === 0 ? (
          <div className="text-center text-gray-500 py-4">No security keys registered</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {keys.map(key => (
              <div key={key.id} className="py-3 flex items-center justify-between">
                <div className="flex-1">
                  {renamingKey === key.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        onKeyDown={(e) => e.key === 'Enter' && handleRename(key.id)}
                      />
                      <button onClick={() => handleRename(key.id)} className="text-xs text-primary">Save</button>
                      <button onClick={() => setRenamingKey(null)} className="text-xs text-gray-500">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-gray-900">
                        {key.device_name}
                        <button
                          onClick={() => { setRenamingKey(key.id); setRenameValue(key.device_name); }}
                          className="ml-2 text-xs text-gray-400 hover:text-gray-600"
                        >
                          rename
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Added {new Date(key.created_at).toLocaleDateString()}
                        {key.last_used_at && ` · Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(key.id, key.device_name)}
                  className="ml-4 text-sm text-red-600 hover:text-red-900"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const { theme, setTheme, themes } = useTheme();
  const [tab, setTab] = useState('Profile');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  
  const [primaryColor, setPrimaryColor] = useState('#4F46E5');
  const [secondaryColor, setSecondaryColor] = useState('#7C3AED');
  const [darkMode, setDarkMode] = useState(false);
  
  const [loginHistory, setLoginHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [revokingSession, setRevokingSession] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });

  // Change Password Form State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // MFA Disable State & Ref
  const [mfaCodeToDisable, setMfaCodeToDisable] = useState('');
  const mfaCodeRef = useRef('');

  const PRESET_COLORS = [
    '#4F46E5', '#0EA5E9', '#059669', '#DC2626',
    '#D97706', '#7C3AED', '#DB2777', '#2563EB',
  ];

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (tab === 'Security' && loginHistory.length === 0) {
      loadLoginHistory();
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      if (response.status === 'success' && response.user) {
        setProfile(response.user);
        if (response.user.primary_color) {
          setPrimaryColor(response.user.primary_color);
          document.documentElement.style.setProperty('--color-primary', response.user.primary_color);
        } else {
          setPrimaryColor('#4F46E5');
          document.documentElement.style.setProperty('--color-primary', '#4F46E5');
        }
        if (response.user.secondary_color) {
          setSecondaryColor(response.user.secondary_color);
          document.documentElement.style.setProperty('--color-secondary', response.user.secondary_color);
        } else {
          setSecondaryColor('#7C3AED');
          document.documentElement.style.setProperty('--color-secondary', '#7C3AED');
        }
        if (response.user.dark_mode) {
          setDarkMode(response.user.dark_mode);
          document.documentElement.setAttribute('data-mode', 'dark');
        } else {
          setDarkMode(false);
          document.documentElement.removeAttribute('data-mode');
        }
      } else {
        setError(response.error || 'Failed to load profile');
      }
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadLoginHistory = async () => {
    try {
      const res = await api.get('/security/my/login-history?limit=10');
      setLoginHistory(res.data.login_history || []);
    } catch (err) {
      console.error('Failed to load login history:', err);
    }
  };

  const loadSessions = useCallback(async () => {
    try {
      const data = await authAPI.listSessions();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);
      if (response.status === 'success') {
        setPasswordSuccess('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setPasswordError(response.error || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, or GIF file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await profileAPI.uploadProfilePicture(formData);
      if (response.status === 'success') {
        setSuccess('Profile picture updated successfully');
        setProfile(prev => ({
          ...prev,
          profile_picture_url: response.profile_picture_url
        }));
        // Notify NavBar to refresh the avatar
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        setError(response.error || 'Failed to upload profile picture');
      }
    } catch (error) {
      setError(error.message || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePicture = async () => {
    setConfirmDialog({
      open: true,
      title: 'Remove Profile Picture',
      message: 'Are you sure you want to remove your profile picture?',
      variant: 'danger',
      onConfirm: async () => {
        setError('');
        setSuccess('');
        try {
          const response = await profileAPI.removeProfilePicture();
          if (response.status === 'success') {
            setSuccess('Profile picture removed');
            setProfile(prev => ({ ...prev, profile_picture_url: null }));
            // Notify NavBar to refresh the avatar
            window.dispatchEvent(new Event('profileUpdated'));
          } else {
            setError(response.error || 'Failed to remove profile picture');
          }
        } catch (error) {
          setError(error.message || 'Failed to remove profile picture');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  const handleDisableMFA = async () => {
    setMfaCodeToDisable('');
    mfaCodeRef.current = '';
    setConfirmDialog({
      open: true,
      title: 'Disable Two-Factor Authentication',
      message: 'Are you sure you want to disable MFA? This will make your account significantly less secure. Please enter your current 6-digit authenticator code to confirm:',
      variant: 'danger',
      showMfaInput: true,
      onConfirm: async () => {
        try {
          const response = await authAPI.disableMFA(mfaCodeRef.current);
          if (response.status === 'success') {
            setSuccess('MFA has been disabled');
            setProfile(prev => ({
              ...prev,
              mfa_enabled: false
            }));
          } else {
            setError(response.error || 'Failed to disable MFA');
          }
        } catch (error) {
          setError(error.message || 'Failed to disable MFA');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  const handleColorChange = async (color) => {
    setPrimaryColor(color);
    document.documentElement.style.setProperty('--color-primary', color);
    try {
      await profileAPI.updatePreferences({ primary_color: color });
      setError(''); // Clear any previous errors
      setSuccess('Primary color updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess(''); // Clear any previous success
      setError('Failed to save color preference');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleSecondaryColorChange = async (color) => {
    setSecondaryColor(color);
    document.documentElement.style.setProperty('--color-secondary', color);
    try {
      await profileAPI.updatePreferences({ secondary_color: color });
      setError(''); // Clear any previous errors
      setSuccess('Secondary color updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess(''); // Clear any previous success
      setError('Failed to save color preference');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDarkModeToggle = async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    if (newValue) {
      document.documentElement.setAttribute('data-mode', 'dark');
    } else {
      document.documentElement.removeAttribute('data-mode');
    }
    try {
      await profileAPI.updatePreferences({ dark_mode: newValue });
      setSuccess(newValue ? 'Dark mode enabled' : 'Dark mode disabled');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save dark mode preference');
    }
  };

  const handleRevokeSession = async (sessionDbId) => {
    setRevokingSession(sessionDbId);
    try {
      await authAPI.revokeSession(sessionDbId);
      setSessions(prev => prev.filter(s => s.id !== sessionDbId));
      setSuccess('Session revoked');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageLayout title="Account">
      {error && <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-700">{error}</div>}
      {success && <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4 text-sm text-green-700">{success}</div>}

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
        <div className="border-b border-gray-200 flex">
          {['Profile', 'Appearance', 'Security'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Tab */}
      {tab === 'Profile' && profile && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-24 w-24 relative">
                {(() => {
                  const avatarColor = getAvatarColor(profile);
                  return (
                    <div className={`h-24 w-24 rounded-full ${avatarColor.className} text-white flex items-center justify-center text-2xl font-medium relative overflow-hidden`} style={avatarColor.style}>
                      <span>{getInitials(profile)}</span>
                      {profile.profile_picture_url && (
                        <img
                          className="absolute inset-0 h-24 w-24 rounded-full object-cover"
                          src={profile.profile_picture_url}
                          alt=""
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                  );
                })()}
                <button
                  onClick={() => fileInputRef.current.click()}
                  disabled={uploading}
                  className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-lg hover:bg-gray-100"
                  title="Change picture"
                >
                  <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/jpeg,image/png,image/gif"
                  onChange={handleFileSelect}
                />
              </div>
              <div className="ml-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {profile.firstname} {profile.lastname}
                </h3>
                <p className="text-sm text-gray-500">{profile.email}</p>
                {profile.profile_picture_url && (
                  <button
                    onClick={handleRemovePicture}
                    className="mt-1 text-xs text-red-500 hover:text-red-700"
                  >
                    Remove picture
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 px-6 py-5">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={profile.firstname || ''}
                  onChange={(e) => setProfile({...profile, firstname: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={profile.lastname || ''}
                  onChange={(e) => setProfile({...profile, lastname: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={profile.phone || ''}
                  onChange={(e) => setProfile({...profile, phone: e.target.value})}
                  placeholder="+61 400 000 000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select
                  value={profile.timezone || 'UTC'}
                  onChange={(e) => setProfile({...profile, timezone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="UTC">UTC</option>
                  <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                  <option value="Australia/Melbourne">Australia/Melbourne (AEST/AEDT)</option>
                  <option value="Australia/Brisbane">Australia/Brisbane (AEST)</option>
                  <option value="Australia/Perth">Australia/Perth (AWST)</option>
                  <option value="Australia/Adelaide">Australia/Adelaide (ACST/ACDT)</option>
                  <option value="America/New_York">America/New York (EST/EDT)</option>
                  <option value="America/Los_Angeles">America/Los Angeles (PST/PDT)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                  <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                </select>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await profileAPI.updateProfile({
                    firstname: profile.firstname,
                    lastname: profile.lastname,
                    phone: profile.phone,
                    timezone: profile.timezone
                  });
                  setSuccess('Profile updated successfully');
                  setTimeout(() => setSuccess(''), 3000);
                } catch (err) {
                  setError(err.message || 'Failed to update profile');
                }
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover text-sm font-medium"
            >
              Save Profile
            </button>
          </div>

          <div className="border-t border-gray-200 px-6 py-5">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{profile.email}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Assigned Roles</dt>
                <dd className="mt-1 flex flex-wrap gap-1">
                  {profile.roles && profile.roles.length > 0 ? (
                    profile.roles.map(role => (
                      <Badge key={role} color={role === 'Admin' ? 'blue' : 'green'}>{role}</Badge>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400 italic">No assigned roles</span>
                  )}
                </dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Account Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(profile.created_at).toLocaleDateString()}
                </dd>
              </div>
              {/* MFA — hidden for SSO users (Decision 15: MFA handled by Microsoft) */}
              {profile.auth_method !== 'sso' && (!profile.oauth_providers || profile.oauth_providers.length === 0) && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Two-Factor Authentication</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge color={profile.mfa_enabled ? 'green' : 'yellow'}>{profile.mfa_enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </div>
                    <div>
                      {profile.mfa_enabled && (
                        <button
                          onClick={handleDisableMFA}
                          className="text-sm text-red-600 hover:text-red-900"
                        >
                          Disable MFA
                        </button>
                      )}
                    </div>
                  </div>
                </dd>
              </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* Appearance Tab */}
      {tab === 'Appearance' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Theme</h3>
            <div className="flex gap-3">
              {Object.entries(themes).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => setTheme(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    theme === key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 px-6 py-5">
            <h3 className="text-lg font-medium text-gray-900 mb-1">Primary Color</h3>
            <p className="text-sm text-gray-500 mb-4">Choose the accent color for buttons, links, and badges across the portal</p>
            <div className="flex flex-wrap gap-3 items-center">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorChange(color)}
                  className={`w-10 h-10 rounded-full transition-transform hover:scale-110 ${
                    primaryColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
              <div className="flex items-center gap-2 ml-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  title="Custom color"
                />
                <span className="text-sm text-gray-500 font-mono">{primaryColor}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 px-6 py-5">
            <h3 className="text-lg font-medium text-gray-900 mb-1">Secondary Color</h3>
            <p className="text-sm text-gray-500 mb-4">Choose the secondary color for gradients and accents</p>
            <div className="flex flex-wrap gap-3 items-center">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => handleSecondaryColorChange(color)}
                  className={`w-10 h-10 rounded-full transition-transform hover:scale-110 ${
                    secondaryColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
              <div className="flex items-center gap-2 ml-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => handleSecondaryColorChange(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  title="Custom color"
                />
                <span className="text-sm text-gray-500 font-mono">{secondaryColor}</span>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Live Gradient Preview</p>
              <div 
                className="h-32 rounded-lg shadow-lg relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-center px-4">
                    <div className="text-lg font-semibold mb-1">Gradient Preview</div>
                    <div className="text-sm opacity-90">This gradient uses your primary and secondary colors</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Dark Mode</h3>
                <p className="text-sm text-gray-500 mt-1">Reduces eye strain in low-light environments</p>
              </div>
              <button
                onClick={handleDarkModeToggle}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {tab === 'Security' && (
        <div className="space-y-6">
          {/* Change Password Card — hidden for SSO users (Decision 15) */}
          {profile.auth_method !== 'sso' && (!profile.oauth_providers || profile.oauth_providers.length === 0) && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
              <p className="text-sm text-gray-500 mt-1">Update your account password</p>
            </div>
            <div className="p-6">
              {passwordSuccess && <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4 text-sm text-green-700">{passwordSuccess}</div>}
              {passwordError && <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-700">{passwordError}</div>}

              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover text-sm font-medium disabled:opacity-50"
                >
                  {changingPassword ? 'Updating...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
          )}

          <SecurityKeysCard />

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Active Sessions</h3>
              <p className="text-sm text-gray-500 mt-1">Manage your active login sessions across devices</p>
            </div>
            <div className="divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">No active sessions</div>
              ) : (
                sessions.map(session => (
                  <div key={session.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{session.user_agent || 'Unknown device'}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {session.ip_address} • {formatDate(session.created_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revokingSession === session.id}
                      className="ml-4 text-sm text-red-600 hover:text-red-900 disabled:opacity-50"
                    >
                      {revokingSession === session.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Login Activity</h3>
              <p className="text-sm text-gray-500 mt-1">Last 10 login attempts on your account</p>
            </div>
            <div className="divide-y divide-gray-200">
              {loginHistory.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">No login history available</div>
              ) : (
                loginHistory.map((entry, idx) => (
                  <div key={idx} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm text-gray-900">{formatDate(entry.created_at || entry.timestamp)}</div>
                        <div className="text-xs text-gray-500 mt-1">{entry.ip_address} • {entry.auth_method || entry.method || 'Local'}</div>
                      </div>
                      <Badge color={entry.success ? 'green' : 'red'}>{entry.success ? '✓ Success' : '✗ Failed'}</Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
        variant={confirmDialog.variant}
      >
        {confirmDialog.showMfaInput && (
          <div className="pt-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">MFA Code</label>
            <input
              type="text"
              maxLength={6}
              value={mfaCodeToDisable}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setMfaCodeToDisable(val);
                mfaCodeRef.current = val;
              }}
              placeholder="000000"
              className="w-full px-4 py-2 text-center text-xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary tracking-widest placeholder-gray-300"
              required
            />
          </div>
        )}
      </ConfirmDialog>
    </PageLayout>
  );
};

export default SettingsPage;
